import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import { asArray } from "@/lib/asArray";
import React, { useEffect, useState } from "react";
import { fmtDateTimeFirm, fmtDate, fmtMonth } from "@/lib/dt";   // 5c · F-5
import api, { API_URL, toErrMsg } from "@/lib/api";
import { useEngagement } from "@/contexts/EngagementContext";
import { useI18n } from "@/contexts/I18nContext";
import StoredResultBanner from "@/components/StoredResultBanner";   // Batch 27 · Add-on 2 Step 2
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  Upload, Info, TableProperties, Download, ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import InventoryUploadDialog from "./InventoryUploadDialog";
import ReadoutCard from "./ReadoutCard";   // D12 — AI Readout
import FyPeriodNote, { fyBlocks } from "./FyPeriodNote";   // STEP 7b · F6: the financial year analysed + the PY comparison
import { fmtNumber, fmtCount } from "@/components/Amount";   // STEP 3 · rule 2 · B-2e: the one amount formatter
import { jobErrorText } from "@/lib/jobErrorText";   // Step 11d · A2: a failed job's error in the screen's language

const POLL_MS = 2000;
const fmtN = (n, digits = 2) => n == null
  ? "—"
  : fmtNumber(n, { decimals: digits });
const fmtPct = (n) => n == null ? "—" : `${(n * 100).toFixed(2)}%`;

const CHECKS = [
  { key: "turnover_days_trend",  labelKey: "inventoryTab.turnoverLabel", testid: "inventory-turnover" },
  { key: "slow_moving_pct_trend", labelKey: "inventoryTab.slowLabel",    testid: "inventory-slow" },
  { key: "negative_stock_frequency", labelKey: "inventoryTab.negLabel",  testid: "inventory-neg" },
];

export default function InventoryTab() {
  const { current } = useEngagement();
  const { t, lang } = useI18n();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [drilldown, setDrilldown] = useState(null);
  const [drillRows, setDrillRows] = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);
  const [activeCheck, setActiveCheck] = useState(CHECKS[0].key);

  const load = async () => {
    if (!current) return;
    setLoading(true);
    try {
      const r = await api.get(
        `/digital-audit/${current.id}/inventory/analytics`, { _silent: true });   // Batch 33 · T1: 404 = the empty state below
      setDoc(r.data || null);
    } catch (e) {
      if (e?.response?.status === 404) setDoc(null);
      else toast.error(toErrMsg(e, t("inventoryTab.tLoadFailed")));
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [current?.id]);

  const pollJob = async (jobId) => {
    let elapsed = 0;
    const MAX = 15 * 60 * 1000;
    while (elapsed < MAX) {
      await new Promise((r) => setTimeout(r, POLL_MS));
      elapsed += POLL_MS;
      const jr = await api.get(`/jobs/${jobId}`);
      if (jr.data?.status === "done") return jr.data;
      if (jr.data?.status === "failed")
        throw new Error(jobErrorText(jr.data, lang, t, t("inventoryTab.tJobFailed")));
    }
    throw new Error(t("inventoryTab.tJobTimedOut"));
  };

  const recompute = async () => {
    if (!current) return;
    setRunning(true);
    try {
      const r = await api.post(
        `/digital-audit/${current.id}/inventory/recompute`);
      const jobId = r.data?.job_id;
      if (!jobId) throw new Error(t("inventoryTab.tNoJobId"));
      toast.info(t("inventoryTab.tRecomputeQueued"));
      await pollJob(jobId);
      toast.success(t("inventoryTab.tRecomputed"));
      await load();
    } catch (e) {
      toast.error(toErrMsg(e, t("inventoryTab.tRecomputeFailed")));
    } finally { setRunning(false); }
  };

  const openDrill = async (check, period) => {
    if (!current) return;
    setDrilldown({ check, period });
    setDrillRows([]); setDrillLoading(true);
    try {
      const r = await api.get(
        `/digital-audit/${current.id}/inventory/drilldown`,
        { params: { check, period, limit: 200 } });
      const list = r.data?.rows || [];
      list._meta = {
        has_more:     !!r.data?.has_more,
        total:        r.data?.total,
        returned:     r.data?.returned,
        truncated_at: r.data?.truncated_at,
      };
      setDrillRows(list);
    } catch (e) {
      toast.error(toErrMsg(e, t("inventoryTab.tDrilldownFailed")));
    } finally { setDrillLoading(false); }
  };

  const downloadBlob = async (url, defaultName, successMsg) => {
    const token = localStorage.getItem("af_token");
    try {
      const resp = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) {
        toast.error(t("inventoryTab.tDownloadFailedStatus").replace("{status}", String(resp.status)));
        return;
      }
      const blob = await resp.blob();
      const cd = resp.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const fname = (m && m[1]) || defaultName;
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = fname;
      a.click();
      URL.revokeObjectURL(a.href);
      if (successMsg) toast.success(successMsg);
    } catch (e) {
      toast.error(toErrMsg(e, t("inventoryTab.tDownloadFailed")));
    }
  };

  const downloadTemplate = (fmt) =>
    downloadBlob(
      `${API_URL}/digital-audit/inventory/template?fmt=${fmt}`,
      `inventory_template.${fmt}`,
      t("inventoryTab.tTemplateDownloaded").replace("{fmt}", fmt.toUpperCase()));

  const downloadCheck = (check, fmt) => {
    if (!current) return;
    downloadBlob(
      `${API_URL}/digital-audit/${current.id}/inventory/export?check=${check}&fmt=${fmt}`,
      `inventory_${check}.${fmt}`,
      t("inventoryTab.tReportDownloaded").replace("{fmt}", fmt.toUpperCase()));
  };

  if (!current) return null;

  return (
    <TooltipProvider>
    <div className="space-y-4 min-w-0 w-full" data-testid="inventory-tab">
      <Card className="min-w-0">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">
              {t("inventoryTab.title")}
            </CardTitle>
            <div className="flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="outline"
                           data-testid="inventory-template">
                    <Download className="h-3 w-3 me-1" />
                    {t("inventoryTab.downloadTemplate")}
                    <ChevronDown className="h-3 w-3 ms-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => downloadTemplate("csv")}
                                     data-testid="inventory-template-csv">
                    {t("inventoryTab.downloadCsv")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => downloadTemplate("xlsx")}
                                     data-testid="inventory-template-xlsx">
                    {t("inventoryTab.downloadXlsx")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button size="sm" variant="outline"
                       onClick={() => setUploadOpen(true)}
                       data-testid="inventory-upload-open">
                <Upload className="h-3 w-3 me-1" /> {t("inventoryTab.uploadBtn")}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="min-w-0 overflow-x-auto">
          {!loading && (   /* Batch 27 · Add-on 2 Step 2: the shared run-state banner (Run / Rescan) */
            <div className="mb-3">
              <StoredResultBanner lang={lang} state={doc ? (doc.run_state?.state || "fresh") : "not_run"}
                                  runAt={doc?.run_state?.run_at || doc?.computed_at} runBy={doc?.run_state?.run_by_name}
                                  running={running} onRun={recompute} runTestId="inventory-recompute"
                                  testid="inventory-run-state" />
            </div>
          )}
          {!loading && !doc && (
            <div className="text-sm text-muted-foreground"
                 data-testid="inventory-empty">
              {t("inventoryTab.empty")}
            </div>
          )}

          {doc && (
            <div className="space-y-4">
              <div className="text-xs text-muted-foreground">
                {t("inventoryTab.computedRows")
                  .replace("{when}", fmtDateTimeFirm(doc.computed_at))
                  .replace("{n}", fmtCount(doc.row_count ?? 0))}
                {doc.upload && t("inventoryTab.uploadedInfo")
                  .replace("{when}", fmtDateTimeFirm(doc.upload.uploaded_at))
                  .replace("{file}", doc.upload.filename)}
              </div>

              <FyPeriodNote period={doc.fy_period} testid="inventory-fy"
                            blocks={fyBlocks(doc, CHECKS, t, { turnover_days_trend: ["turnover_days", (v) => fmtN(v, 1)], slow_moving_pct_trend: ["slow_pct", fmtPct], negative_stock_frequency: ["rate", fmtPct] }, fmtMonth)} />
              <ReadoutCard engagementId={current?.id} page="inventory" analysisComputedAt={doc.computed_at} />

              <Tabs value={activeCheck} onValueChange={setActiveCheck}>
                <TabsList data-testid="inventory-check-tabs"
                           className="flex flex-wrap h-auto gap-1">
                  {CHECKS.map((c) => (
                    <TabsTrigger key={c.key} value={c.key}
                                  data-testid={`${c.testid}-tabtrigger`}
                                  className="text-base font-bold px-4 py-2">
                      {t(c.labelKey)}
                    </TabsTrigger>
                  ))}
                </TabsList>

                <TabsContent value="turnover_days_trend" className="pt-3">
                  <CheckBlock
                    title={t("inventoryTab.turnoverLabel")}
                    interpretation={doc.turnover_days_trend?.interpretation}
                    columns={[t("inventoryTab.cPeriodEnd"), t("inventoryTab.cInvAvg"),
                               t("inventoryTab.cCogs90d"), t("inventoryTab.cTurnoverDays")]}
                    rows={asArray(doc.turnover_days_trend?.series).map((r) => ({
                      key: r.period_end,
                      cells: [fmtDate(r.period_end),
                              fmtN(r.trailing_inventory_avg),
                              fmtN(r.trailing_cogs),
                              r.turnover_days == null ? "—" : fmtN(r.turnover_days, 1)],
                      drill: r.period_end,
                    }))}
                    testid="inventory-turnover"
                    onDrill={(period) => openDrill("turnover_days_trend", period)}
                    onDownload={(fmt) => downloadCheck("turnover_days_trend", fmt)}
                  />
                </TabsContent>

                <TabsContent value="slow_moving_pct_trend" className="pt-3">
                  <CheckBlock
                    title={t("inventoryTab.slowLabel")}
                    interpretation={doc.slow_moving_pct_trend?.interpretation}
                    subheader={
                      <div className="text-xs text-muted-foreground">
                        <Badge variant="secondary" className="me-2">
                          {t("inventoryTab.slowThresholdBadge").replace("{n}", String(doc.slow_moving_pct_trend?.threshold_days ?? 90))}
                        </Badge>
                      </div>
                    }
                    columns={[t("inventoryTab.cPeriodEnd"), t("inventoryTab.cSlowSku"), t("inventoryTab.cActiveSku"),
                               t("inventoryTab.cSlowValue"), t("inventoryTab.cTotalValue"), t("inventoryTab.cSlowPct")]}
                    rows={asArray(doc.slow_moving_pct_trend?.series).map((r) => ({
                      key: r.period_end,
                      cells: [fmtDate(r.period_end), r.slow_skus, r.active_skus,
                              fmtN(r.slow_value), fmtN(r.total_value),
                              fmtPct(r.slow_pct)],
                      drill: r.period_end,
                    }))}
                    testid="inventory-slow"
                    onDrill={(period) => openDrill("slow_moving_pct_trend", period)}
                    onDownload={(fmt) => downloadCheck("slow_moving_pct_trend", fmt)}
                  />
                </TabsContent>

                <TabsContent value="negative_stock_frequency" className="pt-3">
                  <CheckBlock
                    title={t("inventoryTab.negLabel")}
                    interpretation={doc.negative_stock_frequency?.interpretation}
                    columns={[t("inventoryTab.cPeriod"), t("inventoryTab.cActiveSku"), t("inventoryTab.cNegSku"), t("inventoryTab.cRate")]}
                    rows={asArray(doc.negative_stock_frequency?.series).map((r) => ({
                      key: r.period,
                      cells: [fmtMonth(r.period), r.active_skus, r.negative_skus,
                              fmtPct(r.rate)],
                      drill: r.period,
                    }))}
                    testid="inventory-neg"
                    onDrill={(period) => openDrill("negative_stock_frequency", period)}
                    onDownload={(fmt) => downloadCheck("negative_stock_frequency", fmt)}
                  />
                </TabsContent>
              </Tabs>
            </div>
          )}
        </CardContent>
      </Card>

      {uploadOpen && (
        <InventoryUploadDialog
          engagementId={current.id}
          onClose={() => setUploadOpen(false)}
          onIngested={() => load()}
        />
      )}

      {drilldown && (
        <DrillPeek
          drilldown={drilldown}
          rows={drillRows}
          loading={drillLoading}
          onClose={() => setDrilldown(null)}
        />
      )}
    </div>
    </TooltipProvider>
  );
}

function CheckBlock({ title, interpretation, columns, rows, testid,
                       subheader, onDrill, onDownload }) {
  // Step 11d · add-on: the first column (the item) wide, the figures fixed; one key per check block
  const ckCols = useResizableColumns({ storageKey: `da-${testid}`, defaults: [240, ...columns.slice(1).map(() => 140), 90], mins: [140, ...columns.slice(1).map(() => 96), 60] });
  const { t } = useI18n();
  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="flex items-start justify-between gap-3">
        <div className="text-lg font-bold">{title}</div>
        <div className="flex items-center gap-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <button className="opacity-70 hover:opacity-100"
                       aria-label={t("inventoryTab.interpretation")}>
                <Info className="h-3 w-3" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="left" className="max-w-md">
              <p className="text-xs leading-relaxed"
                 data-testid={`${testid}-interp`}>
                {interpretation || ""}
              </p>
            </TooltipContent>
          </Tooltip>
          {onDownload && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline"
                         data-testid={`${testid}-download`}>
                  <Download className="h-3 w-3 me-1" />
                  {t("inventoryTab.downloadReport")}
                  <ChevronDown className="h-3 w-3 ms-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onDownload("csv")}
                                   data-testid={`${testid}-download-csv`}>
                  {t("inventoryTab.downloadCsv")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onDownload("xlsx")}
                                   data-testid={`${testid}-download-xlsx`}>
                  {t("inventoryTab.downloadXlsx")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
      {subheader}
      <div className="overflow-x-auto min-w-0">
        <ResizableTable cols={ckCols} className="w-full text-sm">
          <thead className="text-start text-xs uppercase text-muted-foreground">
            <tr>
              {columns.map((c, i) => (
                <ResizableTh key={i} index={i}
                     className={`py-2 pe-4 ${i > 0 ? "text-right" : ""}`}>
                  {c}
                </ResizableTh>
              ))}
              <ResizableTh index={columns.length} className="py-2 pe-4"></ResizableTh>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={columns.length + 1}
                       className="py-3 text-center text-muted-foreground text-xs">
                {t("inventoryTab.noData")}
              </td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.key} className="border-t">
                {r.cells.map((c, i) => (
                  <td key={i}
                       className={`py-1.5 pe-4 font-mono text-xs ${i > 0 ? "text-right" : ""}`}>
                    {c}
                  </td>
                ))}
                <td className="py-1.5 pe-4 text-end">
                  {r.drill && (
                    <Button size="sm" variant="ghost"
                             onClick={() => onDrill(r.drill)}
                             data-testid={`${testid}-drill-${r.key}`}>
                      <TableProperties className="h-3 w-3 me-1" /> {t("inventoryTab.drillIn")}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </ResizableTable>
      </div>
    </div>
  );
}

function DrillPeek({ drilldown, rows, loading, onClose }) {
  const colW = useResizableColumns({ storageKey: "da-inventory-drill", defaults: [110, 110, 130, 160, 130, 130], mins: [72, 72, 96, 100, 90, 90] });   // Step 11d · add-on
  const { t } = useI18n();
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
         onClick={onClose}
         data-testid="inventory-drill-modal">
      <div className="max-h-[80vh] w-[min(90vw,900px)] overflow-hidden rounded-md bg-background shadow-lg"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b p-3">
          <div className="text-sm font-medium">
            {t("inventoryTab.drillTitle")
              .replace("{check}", drilldown.check.replaceAll("_", " "))
              .replace("{period}", drilldown.period)}
          </div>
          <Button size="sm" variant="ghost" onClick={onClose}>{t("inventoryTab.close")}</Button>
        </div>
        <div className="overflow-auto p-3 max-h-[70vh]">
          {loading && <div className="text-xs text-muted-foreground">{t("inventoryTab.loading")}</div>}
          {!loading && rows.length === 0 && (
            <div className="text-xs text-muted-foreground">{t("inventoryTab.noRows")}</div>
          )}
          {!loading && rows._meta?.has_more && (
            <div className="mb-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900"
                 data-testid="inventory-drill-truncated">
              {t("inventoryTab.drillTruncated")
                .replace("{n}", String(rows._meta.returned?.toLocaleString?.() ?? rows.length))
                .replace("{total}", String(rows._meta.total?.toLocaleString?.() ?? "?"))
                .replace("{cap}", String(rows._meta.truncated_at ?? 500))}
            </div>
          )}
          {rows.length > 0 && (
            <ResizableTable cols={colW} wrap className="w-full text-xs">
              <thead className="text-start uppercase text-muted-foreground">
                <tr>
                  <ResizableTh index={0} className="py-1 pe-3">{t("inventoryTab.dcType")}</ResizableTh>
                  <ResizableTh index={1} className="py-1 pe-3">{t("inventoryTab.dcTxId")}</ResizableTh>
                  <ResizableTh index={2} className="py-1 pe-3">{t("inventoryTab.dcDate")}</ResizableTh>
                  <ResizableTh index={3} className="py-1 pe-3">{t("inventoryTab.dcSku")}</ResizableTh>
                  <ResizableTh index={4} className="py-1 pe-3 text-end">{t("inventoryTab.dcQty")}</ResizableTh>
                  <ResizableTh index={5} className="py-1 pe-3 text-end">{t("inventoryTab.dcAmount")}</ResizableTh>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-t">
                    <td className="py-1 pe-3">{r.tx_type}</td>
                    <td className="py-1 pe-3 font-mono">{r.tx_id}</td>
                    <td className="py-1 pe-3">{fmtDate(r.tx_date)}</td>
                    <td className="py-1 pe-3">{r.sku_id}{r.sku_name ? ` — ${r.sku_name}` : ""}</td>
                    <td className="py-1 pe-3 text-end font-mono">
                      {fmtN(r.qty)}
                    </td>
                    <td className="py-1 pe-3 text-end font-mono">
                      {fmtN(r.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </ResizableTable>
          )}
        </div>
      </div>
    </div>
  );
}
